import React, { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Center, Image, Text, VStack } from '@chakra-ui/react';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import iconsPng from '@/assets/img/png/icons';
import CustomButton from '@/components/atoms/CustomButton';
import { TRosterUserPeerSupportCertificateInfo } from '@/types/Roster.types';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import useUpgradeMemberToPeerSupport from '@/hooks/peer-support/upgradeMemberToPeerSupport';
import PeerSupportCertificationForm from './PeerSupportCertificationForm';

interface Props {
  peerSupportModalVisible: boolean;
  setPeerSupportModalVisible: Dispatch<SetStateAction<boolean>>;
  organizationId: string;
  userToUpgradeToPeerSupportId: string;
  itemToUpgradeToPeerSupportName: string;
  refetch: () => void;
}

const addPeerSupportCertificationSchema = yup
  .object()
  .shape({
    dateOfIssue: yup.string().required('Date of Issue is required.'),
    validUntil: yup.string().required('Valid Until is required.'),
    certificateId: yup.string().required('Certificate ID is required.'),
  })
  .required();

const PeerSupportUpgradeModal = ({
  peerSupportModalVisible,
  setPeerSupportModalVisible,
  organizationId,
  userToUpgradeToPeerSupportId,
  itemToUpgradeToPeerSupportName,
  refetch,
}: Props) => {
  const methods = useForm<TRosterUserPeerSupportCertificateInfo>({
    resolver: yupResolver(addPeerSupportCertificationSchema),
    mode: 'all',
    defaultValues: {
      dateOfIssue: '',
      validUntil: '',
      certificateId: '',
    },
  });

  const { getValues, reset: resetUserForm, formState } = methods;

  const { isValid } = formState;

  const { isPending, mutate: mutateEmployeeToPeerSupport } = useUpgradeMemberToPeerSupport(() => {
    refetch();
    resetUserForm();
    setPeerSupportModalVisible(false);
  });

  return (
    <ModalContainer
      isOpen={peerSupportModalVisible}
      onClose={() => setPeerSupportModalVisible(false)}
      title="Upgrade Member to Peer Support"
      height={'20vh'}
      width={'40%'}
      backgroundColor={'background.lightBlue'}
    >
      <Center marginTop={'20px'}>
        <Image alt="add-member" src={iconsPng.handshake} width={120} height={120} />
      </Center>
      <VStack marginTop={'40px'}>
        <Text variant={'urbanistSemiBoldLarge'} color={'text.mediumBlue'} maxWidth={'300px'}>
          Add information about {itemToUpgradeToPeerSupportName}’s certification below.
        </Text>
      </VStack>
      <Center>
        <VStack marginY={'40px'} width={FORM_FIELD_WIDTH} gap={'20px'}>
          <PeerSupportCertificationForm methods={methods} />

          <CustomButton
            style={{ marginTop: '20px' }}
            backgroundColor="primary.500"
            label="Upgrade Member"
            width={FORM_FIELD_WIDTH}
            labelColor="white"
            onClick={() => {
              const peerSupportUpgradeInfo: TRosterUserPeerSupportCertificateInfo = getValues();
              mutateEmployeeToPeerSupport({
                organizationId,
                userId: userToUpgradeToPeerSupportId,
                peerSupportUpgradeInfo,
              });
            }}
            disabled={!isValid}
            isLoading={isPending}
          />
        </VStack>
      </Center>
    </ModalContainer>
  );
};

export default PeerSupportUpgradeModal;
